import { defineStore } from "pinia";
import { computed, reactive } from "vue";
import type {
    CartItem,
    CartItemAsset, CartItemCustomer, CartItemMailSender
} from "@/api/modules/autoQ/checkout/types.ts";
import {
    useCheckoutCartLoadingStore
} from "@/stores/checkout/checkoutCartLoadingStore.ts";
import cloneDeep from "lodash/cloneDeep";
import { INITIAL_CART_ITEM } from "@/stores/checkout/constants";

export const useCheckoutCartStore = defineStore("checkoutCart", () => {
    const cartItem = reactive<CartItem>(cloneDeep(INITIAL_CART_ITEM));
    const loadingStore = useCheckoutCartLoadingStore();

    const setCart = (newCartItem: CartItem) => {
        Object.assign(cartItem, newCartItem);
    };

    const setDocuments = (cartAssets: CartItemAsset[]): void => {
        if (!cartItem) return;

        cartItem.documents = cartAssets;
    };

    const setCustomDocuments = (cartAssets: CartItemAsset[]): void => {
        if (!cartItem) return;

        cartItem.uploadedDocuments = cartAssets;
    };

    const setPredefinedDocuments = (cartAssets: CartItemAsset[]): void => {
        if (!cartItem) return;

        cartItem.predefinedDocuments = cartAssets;
    };

    const cartIsEmpty = computed<boolean>(() => {
        if (!cartItem) return true;

        return !allDocumentsCount.value;
    });

    const documentsCount = computed<number>(() => {
        if (!cartItem?.documents || !Array.isArray(cartItem.documents)) return 0;

        return cartItem.documents.length;
    });

    const customDocumentsCount = computed<number>(() => {
        if (!cartItem?.uploadedDocuments || !Array.isArray(cartItem.uploadedDocuments)) return 0;

        return cartItem.uploadedDocuments.length;
    });

    const allDocumentsCount = computed<number>(() => {
        if (!cartItem) return 0;

        return documentsCount.value + customDocumentsCount.value;
    });

    const documents = computed<CartItemAsset[]>(() => {
        return cartItem?.documents ?? [];
    });

    const customDocuments = computed<CartItemAsset[]>(() => {
        return cartItem?.uploadedDocuments ?? [];
    });

    const predefinedDocuments = computed<CartItemAsset[]>(() => {
        return cartItem?.predefinedDocuments ?? [];
    });

    const predefinedDocumentIsInCart = (documentId: number): boolean => {
        if (!cartItem) return false;

        return cartItem.predefinedDocuments.some((document) => document.id === documentId);
    };

    // Derived from the already-loaded document list instead of a per-document network call
    // (GET /checkout/document-exists checks the exact same list server-side) - see
    // AddToCartButton.vue and useCheckoutDocumentManagement.ensureDocumentsLoaded.
    const documentIsInCart = (documentId: number): boolean => {
        if (!cartItem) return false;

        return cartItem.documents.some((document) => document.id === documentId);
    };

    const mailSender = computed<CartItemMailSender | null>(() => {
        if (!cartItem) return null;

        return cartItem.mailSender;
    });

    const setMailSender = (mailSender: CartItemMailSender): void => {
        if (!cartItem) return;

        cartItem.mailSender = mailSender;
    };

    const customerId = computed(() => {
        if (!cartItem) return null;
        if (!cartItem.customer) return null;

        return cartItem.customer.id;
    });

    const setCustomer = (customer: CartItemCustomer): void => {
        if (!cartItem) return;

        cartItem.customer = cloneDeep(customer);
    };

    const recipients = computed<string[]>(() => {
        if (!cartItem) return [];

        return cartItem.recipients;
    });

    const setRecipients = (recipients: string[]) => {
        if (!cartItem) return;

        cartItem.recipients = recipients;
    };

    return {
        cartItem,
        setCart,
        loadingStore,
        setDocuments,
        setCustomDocuments,
        documentsCount,
        customDocumentsCount,
        allDocumentsCount,
        cartIsEmpty,
        documents,
        customDocuments,
        mailSender,
        setPredefinedDocuments,
        predefinedDocuments,
        predefinedDocumentIsInCart,
        documentIsInCart,
        setMailSender,
        customerId,
        setCustomer,
        setRecipients,
        recipients
    };
});